import { EventEmitter } from 'events';
import { Platform } from 'react-native';
import io, { Socket } from 'socket.io-client';

import { env } from '../utils/env';
import { authSessionEvents } from '../utils/helpers/authSessionEvents';
import { getDeviceHash } from '../utils/helpers/deviceHash';
import LS from '../utils/localStorageUtils';

let socket: Socket | null = null;

let socketBoundIdentity: { playerId: string; token: string | null } | null = null;
let sessionRevoked = false;
// Timestamps of reconnects we forced after a server-side disconnect; capped so a server
// that keeps kicking us (rate limit, auth) does not turn into a 1s reconnect loop.
let forcedReconnects: number[] = [];
// The server allows 7 accepted connections per player in a sliding 60s window
// (apps/websocket MAX_RECONNECTION_ATTEMPTS) and answers the 8th with RATE_LIMITED. Rejected
// attempts are not counted, so retrying slowly always gets back in once the oldest accepted
// connection ages out; retrying fast only burns the forced-reconnect budget inside the window.
const RATE_LIMIT_RETRY_DELAYS_MS = [10_000, 20_000, 30_000];
let rateLimited = false;
let rateLimitedRetries = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

function clearReconnectTimer() {
	if (reconnectTimer) {
		clearTimeout(reconnectTimer);
		reconnectTimer = null;
	}
}

function gameSocketConnectionUrl(raw: string): string {
	let trimmed = raw.trim().replace(/\/$/, '');
	if (trimmed.startsWith('ws://')) {
		trimmed = `http://${trimmed.slice('ws://'.length)}`;
	} else if (trimmed.startsWith('wss://')) {
		trimmed = `https://${trimmed.slice('wss://'.length)}`;
	}
	if (/\/game$/i.test(trimmed)) {
		return trimmed;
	}
	return `${trimmed}/game`;
}

export const socketEvents = Platform.OS !== 'web' ? new EventEmitter() : null;

if (socketEvents) {
	console.log('✅ socketEvents EventEmitter created for platform:', Platform.OS);
} else {
	console.log('🌐 No socketEvents (web platform)');
}

export const getSocket = (playerId: string, username: string) => {
	const token = LS.get('access_token');
	let deferConnect = false;

	if (
		socket &&
		socketBoundIdentity &&
		(socketBoundIdentity.playerId !== playerId || socketBoundIdentity.token !== token)
	) {
		console.log('[TOURNAMENT] Socket identity changed, closing old socket', {
			oldPlayerId: socketBoundIdentity.playerId,
			newPlayerId: playerId,
		});
		// Do not open the replacement while the old socket is still registered on the
		// server: the server would see a duplicate and kick the older one, and that
		// kick used to tear down the new session's state too. Close first, connect next tick.
		closeSocket();
		deferConnect = true;
	}

	if (!socket) {
		const baseUrl = env('WS_API_URL');
		if (!baseUrl) {
			throw new Error('[socket] WS_API_URL is empty');
		}
		const socketURL = gameSocketConnectionUrl(baseUrl);

		console.log('[TOURNAMENT] Creating new socket connection', {
			playerId,
			username,
			socketURL,
		});

		socketBoundIdentity = { playerId, token };
		socket = io(socketURL, {
			path: '/game-socket.io',
			auth: { token, deviceHash: getDeviceHash() },
			query: { playerId, username },
			reconnection: true,
			reconnectionAttempts: Infinity,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			timeout: 20000,
			autoConnect: false,
		});

		setupConnectionHandlers(socket);
		sessionRevoked = false;
		if (deferConnect) {
			const s = socket;
			setTimeout(() => s.connect(), 250);
		} else {
			socket.connect();
		}

		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			(window as { socket?: Socket }).socket = socket;
		}
	}

	return socket;
};

const setupConnectionHandlers = (clientSocket: Socket) => {
	clientSocket.removeAllListeners();

	clientSocket.on('disconnect', reason => {
		// Downgraded to debug: transient disconnects (transport close/ping timeout) are normal and
		// auto-recover. The recovery UI is driven separately and is debounced.
		console.debug('[TOURNAMENT] Socket disconnected:', {
			reason,
			socketId: clientSocket.id,
			wasConnected: clientSocket.connected,
		});

		if (reason === 'io server disconnect') {
			// socket.io does not auto-reconnect after a server-initiated disconnect. Unless the
			// server revoked our session, and as long as this is still the app's live socket,
			// reconnect so the lobby/table screens do not sit on a dead socket forever.
			if (sessionRevoked) {
				return;
			}
			clearReconnectTimer();
			if (rateLimited) {
				// Keep recovering until the server lets us back in; the recovery modal stays up
				// meanwhile (websocket:ready clears it).
				rateLimited = false;
				const delay =
					RATE_LIMIT_RETRY_DELAYS_MS[
						Math.min(rateLimitedRetries, RATE_LIMIT_RETRY_DELAYS_MS.length - 1)
					];
				rateLimitedRetries += 1;
				console.warn(`[SOCKET] Rate limited by the server; retrying in ${delay / 1000}s`);
				emitCustomEvent('websocket:recovering', {
					reason: 'rate_limited',
					retryInMs: delay,
					timestamp: new Date().toISOString(),
				});
				reconnectTimer = setTimeout(() => {
					reconnectTimer = null;
					if (socket === clientSocket && !clientSocket.connected && !sessionRevoked) {
						clientSocket.connect();
					}
				}, delay);
				return;
			}
			// Any other server kick (most often: this player opened the game elsewhere and the
			// server dropped this older socket). Capped so two tabs do not keep stealing the
			// connection from each other.
			reconnectTimer = setTimeout(() => {
				reconnectTimer = null;
				const now = Date.now();
				forcedReconnects = forcedReconnects.filter(t => now - t < 60_000);
				if (forcedReconnects.length >= 5) {
					console.warn('[SOCKET] Server keeps disconnecting us; giving up automatic reconnects');
					return;
				}
				if (socket === clientSocket && !clientSocket.connected && !sessionRevoked) {
					forcedReconnects.push(now);
					console.warn('[SOCKET] Reconnecting after server-side disconnect');
					clientSocket.connect();
				}
			}, 1000);
			return;
		}

		emitCustomEvent('websocket:recovering', {
			reason,
			timestamp: new Date().toISOString(),
		});
	});

	clientSocket.on('connect', () => {
		console.log('✅ Connected to WebSocket');
	});

	// The server sends RATE_LIMITED right before kicking us; the disconnect handler reads the flag.
	clientSocket.on('error', data => {
		if (data?.code === 'RATE_LIMITED') {
			rateLimited = true;
		}
	});

	clientSocket.on('websocket:ready', data => {
		console.log('✅ WebSocket ready:', data);
		rateLimitedRetries = 0;

		emitCustomEvent('websocket:ready', {
			timestamp: data.timestamp,
		});
	});

	clientSocket.on('game:recovering', data => {
		console.log('🔄 Game recovering:', data);
	});

	clientSocket.on('game:recovered', data => {
		console.log('✅ Game recovered:', data);
	});

	clientSocket.on('auth:session-revoked', data => {
		sessionRevoked = true;
		console.warn('🔒 Session revoked:', data);
		authSessionEvents.emitInvalidated({
			reason: data?.reason,
			message: data?.message,
			expiresAt: data?.expiresAt ?? null,
			banReason: data?.banReason,
		});
		clientSocket.disconnect();
	});

	// Reconnect logging is escalated on purpose: brief, expected reconnects (tab focus, short
	// network blips, duplicate-socket cleanup) are silent. We only surface a single "connection
	// unstable" warning once reconnection is genuinely struggling (several failed attempts).
	clientSocket.on('reconnect_attempt', attemptNumber => {
		if (attemptNumber >= 3) {
			console.warn(`🔄 Connection unstable - reconnecting (attempt ${attemptNumber})`);
		}
	});

	clientSocket.on('reconnect', attemptNumber => {
		if (attemptNumber >= 3) {
			console.info(`✅ Reconnected after ${attemptNumber} attempts`);
		}
	});

	clientSocket.on('connect_error', error => {
		// socket.io retries automatically; keep this at debug level so it does not spam while
		// briefly disconnected. A sustained outage still surfaces via the reconnect_attempt warning.
		console.debug('Socket connect_error (auto-retrying):', error.message);
	});
};

function emitCustomEvent(eventName: string, data: any) {
	console.log(`🎯 Emitting ${eventName} on ${Platform.OS}`);

	if (Platform.OS === 'web') {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent(eventName, {
					detail: data,
					bubbles: true,
					cancelable: false,
				}),
			);
			console.log(`✅ Web event dispatched: ${eventName}`);
		}
	} else {
		if (socketEvents) {
			socketEvents.emit(eventName, data);
			const count = socketEvents.listenerCount(eventName);
			console.log(`✅ Native event emitted: ${eventName} (${count} listeners)`);
		} else {
			console.error('❌ socketEvents is null!');
		}
	}
}

export const closeSocket = () => {
	if (socket) {
		console.log('[TOURNAMENT] Closing socket...', {
			socketId: socket.id,
			connected: socket.connected,
		});
		socket.removeAllListeners();
		socket.disconnect();
		socket = null;
	}
	clearReconnectTimer();
	rateLimited = false;
	rateLimitedRetries = 0;
	socketBoundIdentity = null;
	if (Platform.OS === 'web' && typeof window !== 'undefined') {
		try {
			delete (window as { socket?: Socket }).socket;
		} catch {
			(window as { socket?: undefined }).socket = undefined;
		}
	}
};
